/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the Navigation component that is displayed at the bottom of 
 * each view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Hooks to use state and create side effects.
 */
import {  useState, useEffect } from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI BottomNavigation component.
 */
import BottomNavigation from '@material-ui/core/BottomNavigation';

/**
 * Material UI BottomNavigationAction component.
 */
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';

/**
 * Used to link to different application routes without causing full page refresh &
 * Hook to get access to the react-router state.
 */
import { Link, useLocation } from 'react-router-dom';

/**
 * Typing for history Location objects.
 */
import { Location } from 'history';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * SVG icon.
 */
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';

/**
 * SVG icon.
 */
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';

/**
 * SVG icon.
 */
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface INavigationProps {
    versionCode: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    tabs: {
        minHeight: '56px'
    },
    tab: {
        width: '100%',
        flex: '1 0 auto'
    },
    favourites: {
        display: 'none'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/**
 * This function gets the specified section of the path.
 * i.e. for "/example/path/name":
 * - index 0: "example"
 * - index 1: "path"
 * - index 2: "name"
 * @param location The location object in question
 * @param index The index of the section
 * @param defaultValue A default value if index is out of bounds.
 */
const getPathNameSection = (location: Location, index: number, defaultValue: string) => {
    if (!location || !location.pathname || !location.pathname.includes('/')) {
        return defaultValue;
    }

    const pathnameSplit = location.pathname.split('/');

    if (index + 1 >= pathnameSplit.length) {
        return defaultValue;
    }

    return `/${pathnameSplit[index + 1]}`;
}

const DEFAULT_NAVIGATION_VALUE = '/search';
const NAVIGATION_SEARCH_VALUE = '/search';
const NAVIGATION_FAVOURITES_VALUE = '/favourites';
const NAVIGATION_BROWSE_VALUE = '/browse';

/*
 * ---------------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------------
 */

const Navigation: React.FunctionComponent<INavigationProps> = ({
    versionCode
}) => {
    // Get current location from react-router state.
    const location = useLocation();

    // Get classnames for styles.
    const classes = useStyles();

    // Set current selected tab value to initial evaluated value.
    const [value, setValue] = useState(() => getPathNameSection(location, 1, DEFAULT_NAVIGATION_VALUE));

    // Every time location changes reevaluate and store selected tab value.
    useEffect(() => {
        setValue(getPathNameSection(location, 1, DEFAULT_NAVIGATION_VALUE));
    }, [location]);

    return (
        <BottomNavigation
            value={value}
            className={classes.tabs}
        >
            <BottomNavigationAction
                value={NAVIGATION_SEARCH_VALUE}
                className={classes.tab}
                label="Search"
                icon={(
                    <FontAwesomeIcon
                        icon={faSearch}
                        fixedWidth
                    />
                )}
                component={Link}
                to={`/${versionCode}${NAVIGATION_SEARCH_VALUE}`}
            />
            <BottomNavigationAction
                value={NAVIGATION_FAVOURITES_VALUE}
                className={classes.favourites}
                label="Favourites"
                icon={(
                    <FontAwesomeIcon
                        icon={faStar}
                        fixedWidth
                    />
                )}
                component={Link}
                to={`/${versionCode}${NAVIGATION_FAVOURITES_VALUE}`}
            />
            <BottomNavigationAction
                value={NAVIGATION_BROWSE_VALUE}
                className={classes.tab}
                label="Browse"
                icon={(
                    <FontAwesomeIcon
                        icon={faList}
                        fixedWidth
                    />
                )}
                component={Link}
                to={`/${versionCode}${NAVIGATION_BROWSE_VALUE}`}
            />
        </BottomNavigation>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default Navigation;


// WEBPACK FOOTER //
// ./src/components/Navigation.tsx